import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'

interface ClaudeAuthorization {
  id: number
  agent_id: number | null
  agent_username: string | null
  status: string
  account_email: string | null
  account_display_name: string | null
  organization_name: string | null
  subscription_type: string | null
  scopes: string[] | null
  expires_at: string | null
  last_refreshed_at: string | null
  created_at: string
  updated_at: string
}

interface Agent {
  id: number
  username: string
}

interface Props {
  authorizations: ClaudeAuthorization[]
  agents: Agent[]
}

const STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  active: 'success',
  pending_oauth: 'warning',
  expired: 'error',
  revoked: 'default',
}

export default function Index({ authorizations, agents }: Props) {
  const [revokeTarget, setRevokeTarget] = useState<ClaudeAuthorization | null>(null)

  function handleRevoke() {
    if (revokeTarget) {
      router.delete(`/manage/claude_authorizations/${revokeTarget.id}`)
      setRevokeTarget(null)
    }
  }

  function handleRefresh(id: number) {
    router.post(`/manage/claude_authorizations/${id}/refresh`)
  }

  function handleAssign(authId: number, agentId: number | '') {
    router.patch(`/manage/claude_authorizations/${authId}`, {
      claude_authorization: { agent_id: agentId === '' ? null : agentId },
    })
  }

  function formatExpiry(expiresAt: string | null) {
    if (!expiresAt) return '-'
    const date = new Date(expiresAt)
    const now = new Date()
    if (date < now) return 'Expired'
    const hours = Math.round((date.getTime() - now.getTime()) / 3600000)
    if (hours < 1) return 'Soon'
    return `${hours}h`
  }

  return (
    <SidebarLayout
      section="manage"
      title="Claude Authorizations"
      action={
        <Button component={Link} href="/manage/claude_authorizations/new" variant="contained">
          New authorization
        </Button>
      }
    >
      {authorizations.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No Claude authorizations. Click "New authorization" to create one.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {authorizations.map((auth) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={auth.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {auth.account_email || auth.account_display_name || `#${auth.id}`}
                    </Typography>
                    {auth.organization_name && (
                      <Typography variant="caption" color="text.secondary">
                        {auth.organization_name}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={auth.status}
                    color={STATUS_COLORS[auth.status] || 'default'}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Assigned Agent
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={auth.agent_id ?? ''}
                      onChange={(e) => handleAssign(auth.id, e.target.value as number | '')}
                      displayEmpty
                      disabled={auth.status !== 'active'}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {agents.map((agent) => (
                        <MenuItem key={agent.id} value={agent.id}>
                          {agent.username}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Expires: {formatExpiry(auth.expires_at)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Refreshed: {auth.last_refreshed_at ? new Date(auth.last_refreshed_at).toLocaleString() : '-'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  {auth.status === 'active' && (
                    <Button size="small" onClick={() => handleRefresh(auth.id)}>
                      Refresh
                    </Button>
                  )}
                  {auth.status !== 'revoked' && (
                    <Button size="small" color="error" onClick={() => setRevokeTarget(auth)}>
                      Revoke
                    </Button>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!revokeTarget}
        title="Revoke authorization"
        message={`Are you sure you want to revoke authorization #${revokeTarget?.id} (${revokeTarget?.account_email || 'unknown'})? The assigned agent will lose access to Claude Code.`}
        onConfirm={handleRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </SidebarLayout>
  )
}
